import { useCallback, useMemo } from "react";
import { useCurrentRapidData } from "@rapid/store";
import { useProjectSelector } from "@rapid/contexts/ProjectSelectorContext";
import {
    useDatasetByRapidDataId,
    useBatchByName,
    useSourcesInBatch
} from "@rapid/queries/datasetQueries";
import type { SourceDocument } from "@rapid/utils/api/datasets";

/**
 * Prediction from source document annotations (COCO format)
 */
export interface SourcePrediction {
    x: number;
    y: number;
    width: number;
    height: number;
    confidence: number;
    class: string;
    class_id: number;
    detection_id: string;
    parent_id?: string;
}

/**
 * Parsed annotation data from source document
 */
export interface SourceAnnotationData {
    image: { width: number; height: number };
    predictions: SourcePrediction[];
}

/**
 * Annotation data stored in a source document under annotations[annotationGroup]
 */
export interface SourceAnnotationEntry {
    original?: {
        annotation?: string; // JSON string of SourceAnnotationData
        format?: string;
        source?: string;
    };
    converted?: string;
    [key: string]: any;
}

export interface ImproveBatchData {
    sources: SourceDocument[];
    batchRef: string;
    datasetId: string;
    /** The annotation group key from the dataset - use this to access source.annotations[annotationGroup] */
    annotationGroup: string;
}

/**
 * Parse annotations from a source document using the dataset's annotation group
 *
 * @param source - The source document from Firestore
 * @param annotationGroup - The annotation group key from dataset.annotation (e.g., "objects", "egg-rapidDataId123")
 * @returns Parsed predictions or null if no annotations available
 */
export function parseSourceAnnotations(
    source: SourceDocument,
    annotationGroup: string
): SourceAnnotationData | null {
    try {
        const annotationEntry = source.annotations?.[annotationGroup];
        if (!annotationEntry) {
            console.warn(
                `[parseSourceAnnotations] No annotation found for group "${annotationGroup}" in source ${source.id}`
            );
            return null;
        }

        // Prefer .converted (latest edits) over .original.annotation (uploaded annotations)
        const convertedJson = annotationEntry.converted;
        const originalJson = annotationEntry.original?.annotation;

        if (convertedJson) {
            const converted = JSON.parse(convertedJson) as {
                key: string;
                boxes: Array<{
                    id: string;
                    label: string;
                    x: number | string;
                    y: number | string;
                    width: number | string;
                    height: number | string;
                    confidence?: number | null;
                    type?: string;
                }>;
                width: number;
                height: number;
            };

            const predictions: SourcePrediction[] = converted.boxes.map((box, index) => ({
                x: typeof box.x === "string" ? parseFloat(box.x) : box.x,
                y: typeof box.y === "string" ? parseFloat(box.y) : box.y,
                width: typeof box.width === "string" ? parseFloat(box.width) : box.width,
                height: typeof box.height === "string" ? parseFloat(box.height) : box.height,
                confidence: box.confidence ?? 1.0,
                class: box.label,
                class_id: index,
                detection_id: box.id
            }));

            return { image: { width: converted.width, height: converted.height }, predictions };
        }

        if (originalJson) {
            return JSON.parse(originalJson) as SourceAnnotationData;
        }

        console.warn(
            `[parseSourceAnnotations] No converted or original.annotation found for source ${source.id}`
        );
        return null;
    } catch (err) {
        console.error(
            `[parseSourceAnnotations] Failed to parse annotations for source ${source.id}:`,
            err
        );
        return null;
    }
}

/**
 * Hook to load improve batch images.
 *
 * Query flow (each step is a react-query hook, sequentially enabled):
 * 1. Get dataset by rapidDataId (direct Firestore)
 * 2. Get "rapid-review" batch from dataset (API)
 * 3. List sources in the batch (API)
 */
export function useImproveBatchLoader() {
    const currentRapidData = useCurrentRapidData();
    const projectSelector = useProjectSelector();
    const rapidDataId = currentRapidData?.id;

    const { data: dataset } = useDatasetByRapidDataId(rapidDataId);
    const datasetId = dataset?.id;

    const { data: batch } = useBatchByName(datasetId, "rapid-review");
    const batchId = batch?.id;

    const { data: sources, isLoading: loading, error } = useSourcesInBatch(datasetId, batchId);

    const annotationGroup = dataset?.annotation ?? null;
    const batchRef = datasetId && batchId ? `${datasetId}/${batchId}` : undefined;

    const improveBatch = useMemo<ImproveBatchData | null>(() => {
        if (!sources || !datasetId || !batchRef || !annotationGroup) return null;
        return { sources: sources as SourceDocument[], batchRef, datasetId, annotationGroup };
    }, [sources, datasetId, batchRef, annotationGroup]);

    const getSourceImageUrl = useCallback(
        (source: SourceDocument): string => {
            const cdnHost = projectSelector.cdnHost();
            return `https://${cdnHost}/${source.owner}/${source.id}/original.jpg`;
        },
        [projectSelector]
    );

    return {
        loading,
        error,
        improveBatch,
        sources: sources ?? [],
        annotationGroup,
        getSourceImageUrl
    };
}

export type { SourceDocument } from "@rapid/utils/api/datasets";
